import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { 
  EnumView, 
  ResolutionCategoryView,
  JobTitleView, 
  JobTitleHstRoleMappingView, 
  RecommendedHstRoleView 
} from './types/dimensionTypes';
import { GetRecommendedHstRoleParams, GetJobTitleHstRoleMappingsParams } from './requests/dimensionRequests';

// Import MSAL instance from your app (adjust the import path if needed)
import { msalInstance } from '../../index';
import { getAzureAdClientId, getTicketsApiBaseUrl } from '../../utils/runtimeConfig';

const apiScope = `${getAzureAdClientId()}/InternalApi`;

// Use only the configured API base URL; do not fallback
const apiBaseUrl = getTicketsApiBaseUrl();

type DimensionTagType =
  | 'TicketDimensions'
  | 'AccessRequestDimensions'
  | 'HstRoles'
  | 'JobTitles'
  | 'Centers'
  | 'AssetDimensions'
  | 'VendorDimensions'
  | 'ComplianceDimensions';

export const dimensionApi = createApi({
  reducerPath: 'dimensionApi',
  baseQuery: fetchBaseQuery({
    baseUrl: apiBaseUrl,
    prepareHeaders: async (headers) => {
      try {
        const account = msalInstance.getActiveAccount();
        if (account) {
          const response = await msalInstance.acquireTokenSilent({
            account,
            scopes: [apiScope],
          });
          headers.set('Authorization', `Bearer ${response.accessToken}`);
        }
      } catch (e) {
        // Optionally log or handle token errors
        console.error('Failed to acquire token for Dimension API:', e);
      }
      return headers;
    },
  }),
  tagTypes: ['TicketDimensions', 'AccessRequestDimensions', 'HstRoles', 'JobTitles', 'Centers', 'AssetDimensions', 'VendorDimensions', 'ComplianceDimensions'],
  endpoints: (builder) => {
    // Most dimensional endpoints are a parameterless GET returning EnumView[], differing only in
    // URL and cache tags. This factory captures that shared shape so each endpoint below stays a
    // single line, keeping the endpoints builder small as new dimension lists are added.
    const buildSimpleListEndpoint = (url: string, tags: DimensionTagType[]) => ({
      query: () => url,
      providesTags: tags,
    });

    return {
      // Ticket dimensional data
      getTicketCategories: builder.query<EnumView[], void>(buildSimpleListEndpoint('ticketCategories', ['TicketDimensions'])),
      getTicketPriorities: builder.query<EnumView[], void>(buildSimpleListEndpoint('ticketPriorities', ['TicketDimensions'])),
      getTicketStatuses: builder.query<EnumView[], void>(buildSimpleListEndpoint('ticketStatuses', ['TicketDimensions'])),
      getTicketUrgencies: builder.query<EnumView[], void>(buildSimpleListEndpoint('ticketUrgencies', ['TicketDimensions'])),

      getResolutionCategories: builder.query<ResolutionCategoryView[], string | void>({
        query: (ticketCategory) => ({
          url: 'resolutionCategories',
          params: ticketCategory ? { ticketCategory } : undefined,
        }),
        providesTags: ['TicketDimensions'],
      }),

      getRecurrenceTypes: builder.query<EnumView[], void>(buildSimpleListEndpoint('recurrenceTypes', ['TicketDimensions'])),

      // Access Request dimensional data
      getAccessRequestStatuses: builder.query<EnumView[], void>(buildSimpleListEndpoint('accessRequestStatuses', ['AccessRequestDimensions'])),
      getArAccessTypes: builder.query<EnumView[], void>(buildSimpleListEndpoint('arAccessTypes', ['AccessRequestDimensions'])),
      getArRequestCategories: builder.query<EnumView[], void>(buildSimpleListEndpoint('arRequestCategories', ['AccessRequestDimensions'])),

      // Centers
      getCenters: builder.query<EnumView[], void>(buildSimpleListEndpoint('centers', ['Centers'])),
      getHstCenters: builder.query<EnumView[], void>(buildSimpleListEndpoint('centers/hst', ['Centers'])),
      getCpsiCenters: builder.query<EnumView[], void>(buildSimpleListEndpoint('centers/cpsi', ['Centers'])),

      // HST Roles and Job Titles
      getHstRoles: builder.query<EnumView[], void>(buildSimpleListEndpoint('hstRoles', ['HstRoles'])),
      getJobTitles: builder.query<JobTitleView[], void>(buildSimpleListEndpoint('jobTitles', ['JobTitles'])),

      getJobTitleHstRoleMappings: builder.query<JobTitleHstRoleMappingView[], GetJobTitleHstRoleMappingsParams>({
        query: (params) => ({
          url: 'jobTitleHstRoleMappings',
          params,
        }),
        providesTags: ['HstRoles', 'JobTitles'],
      }),

      getRecommendedHstRole: builder.query<RecommendedHstRoleView, GetRecommendedHstRoleParams>({
        query: (params) => ({
          url: 'recommendedHstRole',
          params,
        }),
        providesTags: ['HstRoles', 'JobTitles'],
      }),

      // Facility Maintenance dimensional data
      getIssueTypes: builder.query<EnumView[], void>(buildSimpleListEndpoint('issueTypes', ['TicketDimensions'])),
      getRepairableByOptions: builder.query<EnumView[], void>(buildSimpleListEndpoint('repairableByOptions', ['TicketDimensions'])),

      // Custom Application dimensional data
      getApplicationTypes: builder.query<EnumView[], void>(buildSimpleListEndpoint('applicationTypes', ['TicketDimensions'])),

      // Asset dimensional data
      getAssetCategories: builder.query<EnumView[], void>(buildSimpleListEndpoint('assetCategories', ['AssetDimensions'])),
      getAssetStatuses: builder.query<EnumView[], void>(buildSimpleListEndpoint('assetStatuses', ['AssetDimensions'])),
      getPMFrequencies: builder.query<EnumView[], void>(buildSimpleListEndpoint('pmFrequencies', ['AssetDimensions'])),
      getPMCategories: builder.query<EnumView[], void>(buildSimpleListEndpoint('pmCategories', ['AssetDimensions'])),

      getVendorStatusOptions: builder.query<EnumView[], void>(buildSimpleListEndpoint('vendorStatusOptions', ['VendorDimensions'])),

      // Compliance dimensional data
      getComplianceTypes: builder.query<EnumView[], void>(buildSimpleListEndpoint('complianceTypes', ['ComplianceDimensions'])),
      getAccreditationBodies: builder.query<EnumView[], void>(buildSimpleListEndpoint('accreditationBodies', ['ComplianceDimensions'])),
      getComplianceStatuses: builder.query<EnumView[], void>(buildSimpleListEndpoint('complianceStatuses', ['ComplianceDimensions'])),
      getComplianceFrequencies: builder.query<EnumView[], void>(buildSimpleListEndpoint('complianceFrequencies', ['ComplianceDimensions'])),
      getFacilityTypes: builder.query<EnumView[], void>(buildSimpleListEndpoint('facilityTypes', ['ComplianceDimensions'])),
    };
  },
});

export const {
  // Ticket dimensions
  useGetTicketCategoriesQuery,
  useGetTicketPrioritiesQuery,
  useGetTicketStatusesQuery,
  useGetTicketUrgenciesQuery,
  useGetResolutionCategoriesQuery,
  useGetRecurrenceTypesQuery,
  
  // Access Request dimensions
  useGetAccessRequestStatusesQuery,
  useGetArAccessTypesQuery,
  useGetArRequestCategoriesQuery,
  
  // Centers
  useGetCentersQuery,
  useGetHstCentersQuery,
  useGetCpsiCentersQuery,
  
  // HST Roles and Job Titles
  useGetHstRolesQuery,
  useGetJobTitlesQuery,
  useGetJobTitleHstRoleMappingsQuery,
  useGetRecommendedHstRoleQuery,
  
  // Facility Maintenance dimensions
  useGetIssueTypesQuery,
  useGetRepairableByOptionsQuery,

  // Custom Application dimensions
  useGetApplicationTypesQuery,

  // Asset dimensions
  useGetAssetCategoriesQuery,
  useGetAssetStatusesQuery,
  useGetPMFrequenciesQuery,
  useGetPMCategoriesQuery,

  // Vendor dimensions
  useGetVendorStatusOptionsQuery,

  // Compliance dimensions
  useGetComplianceTypesQuery,
  useGetAccreditationBodiesQuery,
  useGetComplianceStatusesQuery,
  useGetComplianceFrequenciesQuery,
  useGetFacilityTypesQuery,
} = dimensionApi;
